import React, { Suspense, lazy, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { ApiProvider, useApi } from './context/ApiContext';
import Header from './components/Header/Header';
import Navigation from './components/Navigation/Navigation';
import Footer from './components/Footer/Footer';
import { PageSkeleton } from './components/Skeleton/Skeleton';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';

// Lazy Load Pages
const Home = lazy(() => import('./pages/Home/Home'));
const FunctionalAreas = lazy(() => import('./pages/FunctionalAreas/FunctionalAreas'));
const OfficeOrder = lazy(() => import('./pages/OfficeOrder/OfficeOrder'));
const Manuals = lazy(() => import('./pages/Manuals/Manuals'));
const ComplianceAuditReports = lazy(() => import('./pages/ComplianceAuditReports/ComplianceAuditReports'));
const PerformanceAuditReport = lazy(() => import('./pages/PerformanceAuditReport/PerformanceAuditReport'));
const SpecialIssueBasedAuditReport = lazy(() => import('./pages/SpecialIssueBasedAuditReport/SpecialIssueBasedAuditReport'));
const NOCAndGO = lazy(() => import('./pages/NOCAndGO/NOCAndGO'));
const NOC = lazy(() => import('./pages/NOC/NOC'));
const SeniorOfficers = lazy(() => import('./pages/SeniorOfficers/SeniorOfficers'));
const OtherOfficers = lazy(() => import('./pages/OtherOfficers/OtherOfficers'));
const Contact = lazy(() => import('./pages/Contact/Contact'));
const NewsList = lazy(() => import('./pages/News/NewsList'));
const NewsDetail = lazy(() => import('./pages/News/NewsDetail'));
const OfficialDetail = lazy(() => import('./pages/Official/OfficialDetail'));
const IntroductionDetail = lazy(() => import('./pages/Introduction/IntroductionDetail'));
const PageDetail = lazy(() => import('./pages/Page/PageDetail'));
const Publications = lazy(() => import('./pages/Publications/Publications'));
const PublicationDetail = lazy(() => import('./pages/Publications/PublicationDetail'));
const AnnualActivityReports = lazy(() => import('./pages/AnnualActivityReports/AnnualActivityReports'));
const FapadNews = lazy(() => import('./pages/FapadNews/FapadNews'));
const Gallery = lazy(() => import('./pages/Gallery/Gallery'));
const SearchResults = lazy(() => import('./pages/SearchResults/SearchResults'));
const MediaCornerList = lazy(() => import('./pages/MediaCorner/MediaCornerList'));
const MediaCornerDetail = lazy(() => import('./pages/MediaCorner/MediaCornerDetail'));
const FinancialAuditReports = lazy(() => import('./pages/FinancialAuditReports/FinancialAuditReports'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));

// Root Layout Component
const RootLayout = () => {
  const api = useApi();

  // Prefetch critical API calls on mount (non-blocking)
  useEffect(() => {
    // Prefetch sliders, settings, and menus in parallel (low priority)
    // This warms up the cache so when components need data, it's already there
    const prefetchData = () => {
      try {
        api.getSliders().catch(() => {});
        api.getSettings().catch(() => {});
        api.getMenus().catch(() => {});
      } catch (err) {
        // Silently fail
      }
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(prefetchData, { timeout: 2000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(prefetchData, 100);
    }
  }, [api]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await api.getSettings();
        
        if (data.seo) {
          if (data.seo.metaTitle) document.title = data.seo.metaTitle;
          
          if (data.seo.metaDescription) {
            let metaDesc = document.querySelector('meta[name="description"]');
            if (metaDesc) {
              metaDesc.setAttribute('content', data.seo.metaDescription);
            } else {
              metaDesc = document.createElement('meta');
              metaDesc.name = "description";
              metaDesc.content = data.seo.metaDescription;
              document.head.appendChild(metaDesc);
            }
          }

          if (data.seo.metaKeywords) {
            let metaKey = document.querySelector('meta[name="keywords"]');
            if (metaKey) {
              metaKey.setAttribute('content', data.seo.metaKeywords);
            } else {
              metaKey = document.createElement('meta');
              metaKey.name = "keywords";
              metaKey.content = data.seo.metaKeywords;
              document.head.appendChild(metaKey);
            }
          }
        }
      } catch (err) {
        console.error('❌ Error fetching SEO settings:', err);
      }
    };
    fetchSettings();
  }, [api]);

  // Track visitor count (once per session) - Defer to not block initial load
  useEffect(() => {
    // Delay visitor tracking to not block initial page load
    const timer = setTimeout(() => {
      const trackVisitor = async () => {
        // Check if visitor already counted in this session
        const visitorTracked = sessionStorage.getItem('visitorTracked');
        
        if (!visitorTracked) {
          try {
            // Use sendBeacon for better performance (non-blocking)
            if (navigator.sendBeacon) {
              navigator.sendBeacon(
                'https://accdfser.vercel.app/api/visitors/increment',
                JSON.stringify({})
              );
              sessionStorage.setItem('visitorTracked', 'true');
            } else {
              // Fallback to fetch
              await fetch('https://accdfser.vercel.app/api/visitors/increment', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
              });
              sessionStorage.setItem('visitorTracked', 'true');
            }
          } catch (err) {
            // Silently fail - don't interrupt user experience
            console.error('❌ Error tracking visitor:', err);
          }
        }
      };
      trackVisitor();
    }, 2000); // Wait 2 seconds after page load

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <ScrollToTop />
      <Header />
      <Navigation />
      <main className="flex-grow">
        <Suspense fallback={<PageSkeleton />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

// Loader for dynamic pages - optimized for speed
const pageLoader = async ({ params }) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    
    const res = await fetch(`https://accdfser.vercel.app/api/pages/slug/${params.slug}`, {
      cache: 'no-store',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!res.ok) {
      if (res.status === 404) {
        return null;
      }
      throw new Error(`Failed to load page: ${res.statusText}`);
    }
    const data = await res.json();
    return data;
  } catch (err) {
    if (err.name === 'AbortError') {
      // Timeout - return null to show skeleton, component will handle retry
      return null;
    }
    return null;
  }
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      { index: true, element: <Home /> },
      { path: "functional-areas", element: <FunctionalAreas /> },
      { path: "office-order", element: <OfficeOrder /> },
      { path: "manuals", element: <Manuals /> },
      { path: "Manual", element: <Manuals /> },
      { path: "Compliance-Audit-Reports", element: <ComplianceAuditReports /> },
      { path: "Performance-Audit-Report", element: <PerformanceAuditReport /> },
      { path: "Special-Issue-Based-Audit-Report", element: <SpecialIssueBasedAuditReport /> },
      { path: "noc-and-go", element: <NOCAndGO /> },
      { path: "NOC", element: <NOC /> },
      { path: "Senior-Officer", element: <SeniorOfficers /> },
      { path: "Other-Officer", element: <OtherOfficers /> },
      { path: "contact", element: <Contact /> },
      { path: "news", element: <NewsList /> },
      { path: "news/:slug", element: <NewsDetail /> },
      { path: "official/:slug", element: <OfficialDetail /> },
      { path: "introduction", element: <IntroductionDetail /> },
      { path: "publications", element: <Publications /> },
      { path: "publications/:idOrSlug", element: <PublicationDetail /> },
      { path: "annual-activity-reports", element: <AnnualActivityReports /> },
      { path: "accdf-army-news", element: <FapadNews /> },
      { path: "gallery", element: <Gallery /> },
      { path: "search", element: <SearchResults /> },
      { path: "media-corner", element: <MediaCornerList /> },
      { path: "media-corner/:slug", element: <MediaCornerDetail /> },
      { path: "Financial-Audit-Reports", element: <FinancialAuditReports /> },
      { 
        path: "page/:slug", 
        element: <PageDetail />,
        loader: pageLoader
      },
      {
        path: "*",
        element: <NotFound />
      }
    ]
  }
]);

function App() {
  return (
    <ApiProvider>
      <RouterProvider router={router} />
    </ApiProvider>
  );
}

export default App;

